import { ActionIcon, Badge, Stack, Tooltip } from "@mantine/core";
import { GridColDef } from "@mui/x-data-grid";
import { PencilIcon, PlusIcon, TrashIcon } from "@phosphor-icons/react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";

import { DataGridBase } from "@/components/DataGridBase";
import { DashboardComponents } from "@/features/dashboard/_components/dashboard";
import { MODAL_HANDLER } from "@/lib/Mantine";
import { Companies, Role, useGetCompaniesQuery } from "@/store";
import { requireRoles } from "@/utils/requiredRoles";

export const Route = createFileRoute("/_private/dashboard/_base/empresas/")({
  component: RouteComponent,
  beforeLoad: requireRoles({
    allowed: [Role.Administrador]
  })
});

function RouteComponent() {
  const navigate = useNavigate();
  const { data, isFetching, refetch } = useGetCompaniesQuery();

  const columns: GridColDef<Companies>[] = [
    {
      field: "id",
      headerName: "Nome",
      minWidth: 150,
      type: "custom",
      display: "flex",
      flex: 1,
      renderCell: ({ value, row }) => {
        const { name } = row;

        return (
          <div className="flex flex-col gap-0.5">
            <p>
              <strong className="mr-1">Código:</strong>
              {value}
            </p>

            <p>{name}</p>
          </div>
        );
      }
    },
    {
      field: "company",
      headerName: "Empresa",
      minWidth: 200,
      type: "custom",
      display: "flex",
      flex: 1,
      renderCell: ({ value, row }) => {
        const { cnpj } = row;

        return (
          <div className="flex flex-col gap-0.5">
            <p>
              <strong>CNPJ:</strong> {cnpj}
            </p>

            <p>
              <strong>Razão Social:</strong> {value}
            </p>
          </div>
        );
      }
    },
    {
      field: "uf",
      headerName: "UF",
      width: 60,
      type: "string",
      align: "center",
      headerAlign: "center"
    },
    {
      field: "city",
      headerName: "Cidade",
      width: 200,
      flex: 1,
      type: "string"
    },
    {
      field: "status",
      headerName: "Status",
      width: 100,
      type: "boolean",
      align: "center",
      headerAlign: "center",
      renderCell: ({ value }) => (
        <Badge color={value ? "green" : "red"} variant="light">
          {value ? "Ativo" : "Inativo"}
        </Badge>
      )
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Ações",
      display: "flex",
      renderCell: ({ row }) => {
        const { id } = row;

        return (
          <ActionIcon.Group>
            <Tooltip label="Carregamentos">
              <ActionIcon color="yellow" variant="light" onClick={() => navigate({ to: "/dashboard/empresas/editar/$id/modal", params: { id } })}>
                <PencilIcon />
              </ActionIcon>
            </Tooltip>

            <Tooltip label="Deletar">
              <ActionIcon onClick={() => MODAL_HANDLER.delete.company({ id })} color="red" variant="light">
                <TrashIcon />
              </ActionIcon>
            </Tooltip>
          </ActionIcon.Group>
        );
      }
    }
  ];

  return (
    <Stack>
      <div className="flex flex-wrap items-center justify-between">
        <DashboardComponents.Title>Gerenciar Empresas</DashboardComponents.Title>

        <div className="flex flex-1 justify-end gap-2 max-md:*:flex-1!">
          <DashboardComponents.RefreshButton tooltipProps={{ label: "Atualizar Empresas" }} onClick={refetch} loading={isFetching} />

          <Tooltip label="Nova Empresa">
            <ActionIcon size="xl" onClick={() => navigate({ to: "/dashboard/empresas/cadastrar/modal" })}>
              <PlusIcon />
            </ActionIcon>
          </Tooltip>
        </div>
      </div>

      <DataGridBase rows={data || []} columns={columns} loading={isFetching} />
    </Stack>
  );
}
